import { z } from "zod";

export const OrgFieldSizeTypeZod = z.enum(["7v7", "9v9", "11v11", "futsal", "other"]);
export type OrgFieldSizeType = z.infer<typeof OrgFieldSizeTypeZod>;

/**
 * A block of time when the field is unavailable — maintenance, tryouts,
 * open training, a tournament, etc. Training schedules using this field
 * will skip any template slot that overlaps one of these blocks.
 *
 * Scope: exactly one of `recurringDaysOfWeek` / `specificDates` / `dateRange`
 * should be populated. If more than one is set, they are unioned.
 */
export const OrgFieldBlockZod = z.object({
  id: z.string(),
  reason: z.string(), // e.g. "Tryouts", "City Maintenance", "Open Training"
  startTime: z.string(), // "HH:mm"
  endTime: z.string(), // "HH:mm"
  /** Applies every week on these days-of-week (0=Sun..6=Sat). */
  recurringDaysOfWeek: z.array(z.number()).optional(),
  /** One-off dates the block applies. "YYYY-MM-DD". */
  specificDates: z.array(z.string()).optional(),
  /** Continuous range of dates the block applies. */
  dateRange: z
    .object({ startDateString: z.string(), endDateString: z.string() })
    .optional(),
  createdAtMS: z.number().optional(),
  updatedAtMS: z.number().optional()
});
export type OrgFieldBlock = z.infer<typeof OrgFieldBlockZod>;

export const OrgFieldZod = z.object({
  id: z.string(),
  orgId: z.string(),
  orgVenueId: z.string(),
  name: z.string(),
  size: z.string().optional(),
  sizeType: OrgFieldSizeTypeZod.optional(),
  details: z.string().optional(),
  /** Dates when the entire field is closed. "YYYY-MM-DD" strings. */
  closedDates: z.array(z.string()).optional(),
  /** Time blocks when the field is reserved/unavailable. */
  blocks: z.array(OrgFieldBlockZod).optional(),
  createdAtMS: z.number(),
  updatedAtMS: z.number()
});

export type OrgField = z.infer<typeof OrgFieldZod>;
